<template>
  <div id="payment-timeline">
    <h3>{{ paymentAdjustedTitle }}</h3>
    <div class="timeline-info-text" v-if="showContents" v-html="refData.paymentAdjustmentInformationText.informationText" />

    <div class="timeline">
      <div v-for="payment in timelinePrices" :key="getKey(payment)" class="timeline-item">
        <p class="price">{{ convertToCurrencyString(payment.price) }}</p>
        <div class="line" />
        <div class="info">
          <label :for="getKey(payment)" class="label">{{ payment.label }}</label>
          <p v-if="payment.date" :id="getKey(payment)" class="date">{{ convertDateToString(new Date(payment.date), false, false) }}</p>
        </div>
      </div>
    </div>
    <div :class="pageBackTo ? 'payment-timeline-buttons' : 'paynow-button'">
      <button v-if="pageBackTo" id="go-back" @click="goBack">Back</button>
      <button id="pay-now" class="secondary" @click="payNow" v-if="timelinePrices[0].price !== 0 && showPayNowBtn">Pay now</button>
    </div>
  </div>
</template>

<script>
import { convertDateToString } from '@general/js/utils/dateTimeUtils';
import { convertToCurrencyString } from '@general/js/utils';

export default {
  name: 'PremiumAdjustmentTimeline',

  props: {
    refData: {
      type: Object,
      default: () => {
        return {}; // make empty object by default so that it doesn't break if refData is not passed
      },
    },
    /**
     * Contains an array of objects that hold timeline data
     * Each object has properties paymentAmount, label and date (optional)
     */
    premiumTimelineData: {
      type: Array,
    },
    isCRM: {
      type: Boolean,
      default: false,
    },

    /**
     * This prop determines whether to show `Go Back` button besides `Pay now` button on premium adjustment timeline
     */
    pageBackTo: {
      type: Boolean,
      default: false,
    },
    /**
     * The page to go to when clicking `Pay now` button on premium adjustment timeline.
     */
    pageTo: {
      type: String,
    },

    /**
     * This is the component that should be shown upon clicking `Cancel` in make a payment form
     */
    homePage: {
      type: String,
    },

    /**
     * Breadcrumbs array for makeAPaymentCRM.vur
     */
    makeAPaymentCrmBreadcrumbs: {
      type: Array,
    },

    /**
     * Prop to control whether to show/hide pay now button
     */
    showPayNowBtn: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {};
  },

  computed: {
    timelinePrices() {
      const timelineData = [];
      this.premiumTimelineData.forEach((element) => {
        const timelineObj = {
          price: element.paymentAmount,
          label: element.label,
        };
        if (element.date) {
          timelineObj.date = element.date;
        }
        timelineData.push(timelineObj);
      });
      return timelineData;
    },
    showContents() {
      if (this.refData) {
        if (this.refData.paymentAdjustmentInformationText) {
          return true;
        }
      }
      return false;
    },
    paymentAdjustedTitle() {
      return this.refData.paymentAdjustmentInformationText?.title ? this.refData.paymentAdjustmentInformationText.title : 'Your payment has been adjusted';
    },
  },

  methods: {
    getKey(data) {
      return `${data.label}-${data.date}`;
    },

    payNow() {
      if (this.isCRM) {
        this.crmNavigatePage(this.pageTo, {
          paymentAmount: this.timelinePrices[0].price,
          homePage: this.homePage,
          breadcrumbs: this.makeAPaymentCrmBreadcrumbs,
        });
      } else {
        this.$emit('handlePayNowBtnClick', this.timelinePrices[0].price);
      }
    },

    goBack() {
      if (this.isCRM) {
        this.$emit('goBackTo');
      }
    },

    /**
     * emit data to parent for page navigation in CRM.
     *
     * @param {string} targetPageName name of target page. it should be one of `PAGES` object value.
     * @param {object} routeParams by default is empty object. if given, it should be object. this object will be used like route param in non CRM build.
     */
    crmNavigatePage(targetPageName, routeParams = {}) {
      window.scrollTo(0, 0);
      this.$emit('crmNavigatePage', targetPageName, routeParams);
    },

    convertToCurrencyString,
    convertDateToString,
  },
};
</script>

<style lang="scss" scoped>
#payment-timeline {
  @include flex-col;
  row-gap: 1.5rem;
  margin-bottom: 1rem;
  max-width: 680px;

  h3,
  p,
  label {
    margin: 0;
  }

  .timeline {
    @include flex-row;
    width: 100%;
    box-sizing: border-box;
    column-gap: 1rem;

    @include mobile {
      @include flex-col;
      row-gap: 1.5rem;
    }

    .timeline-item {
      @include flex-col;
      justify-content: flex-end;
      row-gap: 0.5rem;
      width: rem(144);

      @include mobile {
        width: 100%;

        .info {
          @include flex-row;
          justify-content: space-between;
        }
      }

      &:first-child {
        .line {
          background: $BLUE;
        }
      }

      .line {
        height: rem(8);
        width: 100%;
        border-radius: 20px;
        background: $GREY_500;
      }

      .label,
      .price {
        font-weight: $BOLD;
      }
    }
  }

  .payment-timeline-buttons {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    column-gap: 1.5rem;
    flex-wrap: wrap;
  }
}
</style>
